import { useState, useRef, useEffect, useMemo } from 'react';
import { Chat } from '../utils/api';

// ── Page-scoped suggestion sets ───────────────────────────────────────────────
const PAGE_SUGGESTIONS = {
  'panel-representativeness': [
    'How representative is the panel for my category?',
    'Which age bands are over- or under-indexed?',
    'How does gender skew compare to the national average?',
  ],
  'panel-size': [
    'How has panel size changed over the last 6 months?',
    'What is the current daily unique user count?',
  ],
  'category-behaviour': [
    'How has category penetration trended over the last year?',
    'Which month had the highest active user count?',
    'What is the average session count per user?',
  ],
  'trends-nation': [
    "Which app is growing fastest in my category?",
    'Has overall category usage grown or declined this year?',
    'Show me the top 3 apps by users for the latest month.',
  ],
  'trends-age': [
    'Which age group is growing fastest?',
    'Which age band has the highest penetration?',
    'How have 18–24 users trended over the last 6 months?',
  ],
  'trends-gender': [
    'Is the category skewing more male or female over time?',
    'Which gender has grown more in the last year?',
  ],
  'trends-intensity': [
    'How has the share of Heavy users changed over time?',
    'Are Light users growing or shrinking as a proportion?',
    'What does the intensity mix look like for the latest month?',
  ],
  'loyalty': [
    'What share of users are Solus (single-app) loyalists?',
    'Has multi-app usage (Three+) grown over the past year?',
    'Which loyalty segment is largest?',
  ],
  'competition': [
    'Which two apps share the most users?',
    'What % of users overlap between the top two apps?',
    'Show me the duplication matrix for the latest month.',
  ],
  'sor': [
    'Which app has the highest share of requirements?',
    'How has share of requirements changed over the last 6 months?',
  ],
  'social': [
    'Which social channel reaches the most category buyers?',
    'How does YouTube reach compare to Instagram for my category?',
  ],
  'channel-opp-index': [
    'Which channel has the biggest opportunity gap for this brand?',
    'Where is the brand most under-represented vs. the category?',
    'Which platform should I prioritise for incremental reach?',
  ],
  'by-age': [
    'Which age band has the highest brand penetration?',
    'Where is the biggest penetration gap vs. the category?',
  ],
  'by-gender': [
    'Does the brand skew male or female vs. the category?',
    'Which gender segment has the most growth headroom?',
  ],
  'by-geo': [
    'Which postcode districts have the highest category penetration?',
    'Where is the brand under-penetrated vs. the category?',
  ],
  'category-target-audience': [
    'Who is the core target audience for this category?',
    'Which age and gender combination dominates?',
  ],
  'brand-target-audience': [
    'Who is the brand\'s core audience vs. the category?',
    'Where does my brand over-index vs. the category?',
  ],
  'penetration-targets': [
    'What penetration targets are realistic for the next 6 months?',
    'Which segments have the most headroom to grow?',
  ],
};

const DEFAULT_SUGGESTIONS = [
  "What's the latest month available?",
  'Which app has the highest market share in Food Delivery?',
  'How are Light vs Heavy users trending in Ride Hailing?',
  'Which age group is growing fastest?',
];

// ── Infer prompt builder ──────────────────────────────────────────────────────
function buildInferPrompt(pageLabel, category, month) {
  const parts = ['Analyse what\'s currently shown'];
  if (pageLabel) parts.push(`on the "${pageLabel}" page`);
  if (category)  parts.push(`for ${category}`);
  if (month)     parts.push(`as of ${month}`);
  parts.push('. Highlight the 2–3 most important signals and what action they suggest for a marketing team.');
  return parts.join(' ');
}

// ── Sub-components ────────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div className="chat-typing">
      <span /><span /><span />
    </div>
  );
}

function Message({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`chat-msg ${isUser ? 'chat-msg-user' : 'chat-msg-assistant'}`}>
      {!isUser && (
        <div className="chat-avatar">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
        </div>
      )}
      <div className="chat-bubble">
        {msg.content.split('\n').map((line, i, arr) => (
          <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
        ))}
      </div>
    </div>
  );
}

// ── Main widget ───────────────────────────────────────────────────────────────
export default function ChatWidget({ view, pageLabel, category, month }) {
  const [open,     setOpen]     = useState(false);
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  // Reset conversation when the user navigates to a different page
  useEffect(() => { setMessages([]); }, [view]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const pageContext = useMemo(() => ({
    page:       view       || '',
    page_label: pageLabel  || '',
    category:   category   || '',
    month:      month      || '',
  }), [view, pageLabel, category, month]);

  const suggestions = PAGE_SUGGESTIONS[view] || DEFAULT_SUGGESTIONS;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: msg };
    const history = messages.map(m => ({ role: m.role, content: m.content }));
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const { response } = await Chat.send(msg, history, pageContext);
      setMessages(prev => [...prev, { role: 'model', content: response }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'model',
        content: 'Sorry, I encountered an error. Please try again.',
      }]);
    } finally {
      setLoading(false);
    }
  };

  const infer = () => send(buildInferPrompt(pageLabel, category, month));

  return (
    <>
      {/* ── Floating button ── */}
      <button
        className={`chat-fab ${open ? 'chat-fab-open' : ''}`}
        onClick={() => setOpen(o => !o)}
        title={open ? 'Close chat' : 'Ask the data'}
      >
        {open ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
          </svg>
        )}
      </button>

      {/* ── Chat panel ── */}
      {open && (
        <div className="chat-panel">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <div className="chat-header-dot" />
              <div>
                <div className="chat-header-title">Ask the Data</div>
                <div className="chat-header-sub">
                  {pageLabel
                    ? `${pageLabel}${category ? ` · ${category}` : ''}`
                    : 'Powered by Gemini · Omni Growth'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {/* Infer button */}
              <button
                onClick={infer}
                disabled={loading}
                title="Auto-analyse this page"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  background: 'var(--teal, #1DB89A)', color: '#fff',
                  fontSize: 11, fontWeight: 700, padding: '5px 10px',
                  borderRadius: 6, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1, letterSpacing: '.03em',
                }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                </svg>
                Infer
              </button>
              {/* New chat */}
              {messages.length > 0 && (
                <button
                  className="chat-clear"
                  onClick={() => setMessages([])}
                  title="New conversation"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="1 4 1 10 7 10"/><polyline points="23 20 23 14 17 14"/>
                    <path d="M20.49 9A9 9 0 005.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 013.51 15"/>
                  </svg>
                  New chat
                </button>
              )}
            </div>
          </div>

          {/* Page context pill */}
          {pageLabel && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderBottom: '1px solid var(--border)',
              background: 'rgba(29,184,154,0.04)',
            }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--teal,#1DB89A)" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/>
              </svg>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                Scoped to <strong style={{ color: 'var(--text)' }}>{pageLabel}</strong>
                {category && <> · <strong style={{ color: 'var(--text)' }}>{category}</strong></>}
                {month    && <> · {month}</>}
              </span>
            </div>
          )}

          {/* Messages */}
          <div className="chat-messages">
            {messages.length === 0 && (
              <div className="chat-empty">
                <div className="chat-empty-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                  </svg>
                </div>
                <div className="chat-empty-title">
                  {pageLabel ? `Ask about ${pageLabel}` : 'Ask anything about your data'}
                </div>
                <div className="chat-suggestions">
                  {suggestions.map(s => (
                    <button key={s} className="chat-suggestion" onClick={() => send(s)}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => <Message key={i} msg={m} />)}
            {loading && (
              <div className="chat-msg chat-msg-assistant">
                <div className="chat-avatar">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
                  </svg>
                </div>
                <div className="chat-bubble"><TypingDots /></div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="chat-input-row">
            <input
              ref={inputRef}
              className="chat-input"
              type="text"
              placeholder={pageLabel ? `Ask about ${pageLabel}…` : 'Ask about your data…'}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
              disabled={loading}
            />
            <button
              className="chat-send"
              onClick={() => send()}
              disabled={!input.trim() || loading}
              title="Send"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
